import { useRouteError } from 'react-router-dom';

import { useSession } from '@/lib/auth/session';
import { isSuperAdmin } from '@/lib/types';

/**
 * 화면 하나가 그리다 터졌을 때 <b>그 자리</b>에 세우는 판(라우트 {@code errorElement}).
 *
 * <h2>왜 필요한가 — 지금은 라우터의 맨살이 나온다</h2>
 * {@code errorElement} 가 없으면 react-router 가 자기 기본 화면을 세운다: 검은 글씨의
 * <b>"Unexpected Application Error!"</b> 와 스택. 사이드바도 없어 돌아갈 길이 없고, 사용자에게는
 * 「자료 입력을 눌렀더니 에러 화면」 으로만 보인다(2026-09-02 운영 지적).
 *
 * <h2>배포 직후의 조각 실패는 <b>고장이 아니다</b></h2>
 * 화면 코드는 눌렀을 때 받아온다({@code screens.tsx} 의 {@code lazy}). 그런데 새로 배포하면 조각
 * 파일 이름(해시)이 바뀌고 옛 파일은 사라진다 — <b>배포 전에 열어 둔 탭</b>은 옛 이름을 들고 있으므로,
 * 그 탭에서 아직 안 눌러 본 화면을 누르는 순간 {@code Failed to fetch dynamically imported module}
 * 로 죽는다. 권한과 아무 상관이 없어 <b>최고관리자도 똑같이</b> 막히고, 새로고침 한 번이면 낫는다.
 *
 * <p>그래서 그때는 <b>말없이 한 번 새로 고친다</b>. 사람에게 "새로고침 하세요" 라고 적어 두면 그
 * 문장을 읽는 것이 곧 사용자의 일이 된다. 고리에 빠지지 않게 <b>30초에 한 번</b>만 시도하고, 그래도
 * 안 되면 아래 판을 세운다.
 *
 * <h2>사유는 최고관리자에게만</h2>
 * 화면에는 「불러오지 못했다」 한 줄이면 된다. 스택은 콘솔과 최고관리자 화면에만 남긴다.
 */
export function RouteErrorPage() {
  const error = useRouteError();
  const { data: session } = useSession();

  const detail = error instanceof Error ? `${error.name}: ${error.message}` : String(error);

  if (isStaleChunk(error) && reloadOnce()) {
    return null;
  }

  console.error('[route]', error);

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
      <div>
        <p className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
          화면을 불러오지 못했습니다
        </p>
        {isSuperAdmin(session?.user?.userType) && (
          <p className="mt-1 text-xs font-mono" style={{ color: 'var(--text-muted)', wordBreak: 'break-all' }}>
            {detail}
          </p>
        )}
      </div>
      <div className="flex gap-2">
        <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
          다시 시도
        </button>
        <button type="button" className="btn-secondary" onClick={() => { window.location.href = '/home'; }}>
          홈으로
        </button>
      </div>
    </div>
  );
}

/**
 * 옛 조각을 부르다 죽은 것인가 — 브라우저마다 문구가 달라 넷을 다 본다.
 * (Chrome/Safari: {@code Failed to fetch dynamically imported module} ·
 *  Firefox: {@code error loading dynamically imported module} ·
 *  Safari 구판: {@code Importing a module script failed} · 번들러: {@code ChunkLoadError})
 */
function isStaleChunk(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;
  return /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(error.message);
}

/** 30초에 한 번만 새로 고친다. 조각이 아니라 서버가 죽은 것이면 고리에 빠지므로. */
function reloadOnce(): boolean {
  const KEY = 'route-chunk-reload-at';
  const last = Number(sessionStorage.getItem(KEY) ?? 0);
  if (Date.now() - last < 30_000) return false;
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
  return true;
}
